import { Stack } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useUpdateIntakeQuestionOptionMutation } from "src/api/services/intakeQuestionOptionsApi";
import { useGetIntakeQuestionsQuery } from "src/api/services/intakeQuestionsApi";
import {
  InputType,
  IntakeQuestionOption,
  IntakeQuestionOptionUpdate,
  Question,
} from "src/api/types";
import Input from "src/components/FormInput";
import { MatchParam } from "src/constants/matches";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import DrawerForm from "src/shared/DrawerForm";

interface Props {
  onClose: () => void;
  open: boolean;
  option: IntakeQuestionOption;
}

const useInputs = () => {
  const [values, setValues] = useState<IntakeQuestionOptionUpdate>({
    next_question: null,
    sort_order: 0,
  });
  const { intakeId } = useParams<MatchParam<"INTAKE_ID">>();

  const setFields = useCallback(
    <K extends keyof IntakeQuestionOptionUpdate>(
      key: K,
      value: IntakeQuestionOptionUpdate[K],
    ) => {
      setValues((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const { questions } = useGetIntakeQuestionsQuery(
    intakeId ? Number(intakeId) : skipToken,
    {
      selectFromResult: ({ data }) => {
        return { questions: data?.map((q) => q) ?? [] };
      },
    },
  );

  const inputs: InputType<Question>[] = useMemo(
    () => [
      {
        type: "autocomplete",
        placeholder: "Next Question",
        options: questions,
        value: questions.find((o) => o.slug === values.next_question) ?? null,
        onChange: (v) => setFields("next_question", v?.slug ?? null),
        required: false,
        label: "Next Question",
        getOptionLabel: (q) => `${q.id}: ${q.title}`,
        getOptionDescription: (q) => q.subtitle ?? undefined,
      },
      {
        type: "number",
        name: "sort_order",
        label: "Sort Order",
        onChange: (v) => setFields("sort_order", v),
        required: false,
        value: values.sort_order ?? 0,
      },
    ],
    [questions, setFields, values.next_question, values.sort_order],
  );

  return { inputs, values, setValues };
};

const UpdateOptionDrawer = ({ onClose, open, option }: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { inputs, setValues, values } = useInputs();
  const [updateOption] = useUpdateIntakeQuestionOptionMutation();
  const { intakeId, questionId } =
    useParams<MatchParam<"INTAKE_ID" | "QUESTION_ID">>();
  const errLog = useSentryLogger("error");

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);

      try {
        await updateOption({
          intakeId: Number(intakeId),
          questionId: Number(questionId),
          optionId: option.id,
          body: {
            next_question: values.next_question,
            sort_order: values.sort_order,
          },
        });

        setValues({
          next_question: null,
          sort_order: 0,
        });
        onClose();
      } catch (err) {
        errLog("Could not update option", err);
      } finally {
        setLoading(false);
      }
    },
    [
      errLog,
      intakeId,
      onClose,
      option.id,
      questionId,
      setValues,
      updateOption,
      values,
    ],
  );

  useEffect(() => {
    setValues({
      next_question: option.next_question?.slug,
      sort_order: option.position,
    });
  }, [option.next_question?.slug, option.position, setValues]);

  return (
    <DrawerForm
      label="Update Option"
      open={open}
      onClose={onClose}
      handleSubmit={handleSubmit}
      loading={loading}
    >
      <Stack gap={2} component={"form"}>
        {inputs.map((i) => (
          <Input key={i.label} current={i} />
        ))}
      </Stack>
    </DrawerForm>
  );
};

export default UpdateOptionDrawer;
